import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { Button, Flex, message, Tag } from 'antd';
import { FileDownload } from '../../../assets/images';
import { StarIcon } from '../../../assets/icons/icons';
import { CustomButton, CustomShipmentText } from '../../../components';
import OneTimeCOCAlert from './OneTimeCOCAlert';
import CustomerNotificationCOCRow from './CustomerNotificationCOCRow';
import { ChainOfCustodyItem } from '@/apis/types';
import { CoCEventType } from '@/common/enum';
import CommentAudienceIcons from './COC_Privacy_Comments/CommentAudienceIcons';
import { useToken } from '@/common/helper';
import useUnauthorize from '@/hooks/useUnauthorize';
import { useSpin } from '@/components/SpinContext';
import { ExportReport } from '@/apis/gxpReport';
import { getSystemFileUploadLabel } from './getSystemFileUploadLabel';

interface Shipment {
  custody?: ChainOfCustodyItem[];
}

interface FormattedTime {
  full: string;
  relative: string;
}

interface TimelineItemCTProps {
  custody: ChainOfCustodyItem;
  setShowFull: React.Dispatch<React.SetStateAction<boolean>>;
  showFull: boolean;
  getFormattedTime: (timestamp?: string) => FormattedTime;
  downloadFile: (file: string, fileName: string) => void;
  setHalfHeight: (id: string | number, height: number) => void;
  getNotificationLabel: (method?: string) => string;
  shipment?: Shipment;
  shipmentId: string;
  canViewTranscripts?: boolean;
  canEditAiCommunications?: boolean;
  toggleDrawer?: (
    open: string,
    callTranscripts: string,
    shipmentId: string,
    custody: object
  ) => void;
  fetchSingleShipmentData?: () => void;
}

/**
 * Timeline item component for Chain of Custody display
 *
 * @param custody - The custody data to display
 * @param setShowFull - Function to toggle full/relative time display
 * @param showFull - Whether to show full timestamp or relative time
 * @param getFormattedTime - Function to format timestamps
 * @param downloadFile - Function to handle file downloads
 * @param setHalfHeight - Function to set component height for timeline positioning
 * @param getNotificationLabel - Function to get notification method labels
 * @param shipment - The parent shipment data
 * @param shipmentId - ID of the shipment
 * @param toggleDrawer - Optional function to toggle drawer visibility
 */
const SENSOR_HANDLER_LABELS: Record<string, string> = {
  tive: 'Tive',
  onasset: 'OnAsset',
  roambee: 'Roambee',
  elpro: 'Elpro',
  tempmate: 'Tempmate',
  trackonomy: 'Trackonomy',
};

const TimelineItemCT: React.FC<TimelineItemCTProps> = ({
  custody,
  setShowFull,
  showFull,
  getFormattedTime,
  downloadFile,
  setHalfHeight,
  getNotificationLabel,
  shipment,
  shipmentId,
  toggleDrawer,
  fetchSingleShipmentData,
  canViewTranscripts = true,
  canEditAiCommunications = false,
}) => {
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const { showSpin, hideSpin } = useSpin();
  const systemFileLabel =
    custody?.type === CoCEventType.FileUpload && custody?.created_by === 6
      ? getSystemFileUploadLabel(custody?.file_name)
      : null;

  const parentRef = useRef<HTMLParagraphElement | HTMLDivElement>(null);
  const repliesRef = useRef<HTMLParagraphElement | HTMLDivElement>(null);

  const handleGxpDownload = useCallback(() => {
    if (!custody?.export_url) return;
    showSpin();

    const queryParam = custody.export_url.replace(/^.*\/gxp\/reports\//, '');
    const exportFormat = queryParam.match(/export_format=(\w+)/)?.[1] ?? 'pdf';

    ExportReport(queryParam, token ?? '')
      .then((res) => {
        const blob: Blob = res?.data;
        if (!blob || blob.size === 0) return;

        const disposition: string = res?.headers?.['content-disposition'] ?? '';
        const filenameMatch = disposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
        );
        const filename = filenameMatch
          ? filenameMatch[1].replace(/['"]/g, '').trim()
          : `report.${exportFormat}`;

        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);

        message.success('Export report successfully.');
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  }, [custody?.export_url, token, showSpin, hideSpin, unauthorize]);

  const usesNotificationContentRefs =
    custody?.type === CoCEventType.OneTimeNotification ||
    custody?.type === CoCEventType.CustomerNotification;

  useLayoutEffect(() => {
    const measureHeights = (): void => {
      if (parentRef.current) {
        const parentHeight = parentRef.current.getBoundingClientRect().height;
        const repliesHeight = repliesRef.current
          ? repliesRef.current.getBoundingClientRect().height
          : 0;

        const totalHeight = parentHeight + repliesHeight;
        setHalfHeight(custody.id, totalHeight / 2);
      }
    };

    const rafId = requestAnimationFrame(measureHeights);
    return () => cancelAnimationFrame(rafId);
  }, [custody, shipment?.custody, setHalfHeight]);

  return (
    <>
      <div>
        <div
          ref={usesNotificationContentRefs ? undefined : parentRef}
          className='flex-center btw y w-full relative gap-x-1'
        >
          <Flex align='center' gap={12} className='relative'>
            {/* coc heading and AI icon */}
            <Flex gap={4} align='center'>
              {custody?.type === CoCEventType.OneTimeNotification ? (
                <>
                  <p className={`zl-para-12`}>
                    {custody.is_from_automation
                      ? 'Automation'
                      : 'One-time message'}
                    : {getNotificationLabel(custody?.notification_method) || ''}
                  </p>
                  {custody?.notification_method === 'AI_CALL' ||
                  custody?.notification_method === 'AI_EMAIL' ? (
                    <span className='flex-center AI-star-fill-color'>
                      <StarIcon width={16} height={16} />
                    </span>
                  ) : null}
                </>
              ) : custody?.type === CoCEventType.CustomerNotification ? (
                <p className='zl-para-12'>{custody.title}</p>
              ) : (
                <>
                  <p
                    // inbound change here
                    className={`zl-para-12 flex-center gap-x-1 ${custody?.type === CoCEventType.FileUpload && 'word-break-all'}`}
                  >
                    {systemFileLabel
                      ? systemFileLabel
                      : custody?.handler === 'AWB'
                        ? custody?.action?.replace(/^\[AWB\]\s*/i, '')
                        : SENSOR_HANDLER_LABELS[custody?.handler ?? '']
                          ? `${SENSOR_HANDLER_LABELS[custody?.handler ?? '']} ${(custody?.action ?? '').replace(/^Sensor\s+/i, '')}`
                          : custody?.flight_number
                            ? `${custody.action} (${custody.flight_number})`
                            : custody?.action}

                    {/* // inbound change here AI star with parent */}
                    {custody?.type === CoCEventType.AiCallAlert && (
                      <StarIcon width={16} height={16} />
                    )}
                  </p>
                </>
              )}
            </Flex>
            {custody?.file && custody?.type === CoCEventType.FileUpload && (
              <Button
                type='default'
                className='zl-btn icon-btn-36 radius-full'
                onClick={(e) => {
                  e.stopPropagation();
                  downloadFile(custody.file!, custody.file_name!);
                }}
              >
                <img
                  src={FileDownload}
                  width={16}
                  alt='Download file'
                  className='zl-invert-on-dark'
                />
              </Button>
            )}

            {custody?.export_url &&
              custody?.type === CoCEventType.GxPReport && (
                <Button
                  type='default'
                  className='gxp-report-download'
                  onClick={(e) => {
                    e.stopPropagation();
                    handleGxpDownload();
                  }}
                >
                  <img
                    src={FileDownload}
                    width={16}
                    alt='Download file'
                    className='zl-invert-on-dark'
                  />
                </Button>
              )}
          </Flex>
          <Flex align='center' gap={8}>
            {custody?.type === CoCEventType.Comment && (
              <CommentAudienceIcons
                audienceType={custody?.audience_type ?? ''}
                teams={custody?.audience_teams}
              />
            )}

            <span
              onClick={(e) => {
                setShowFull((prev) => !prev);
                e.stopPropagation();
              }}
              className='cursor-pointer'
            >
              <CustomShipmentText
                text={
                  (showFull
                    ? getFormattedTime(custody?.timestamp)?.full
                    : getFormattedTime(custody?.timestamp)?.relative) ?? ''
                }
                color='var(--text-light)'
                marginBottom='0'
                style={{ whiteSpace: 'nowrap' }}
              />
            </span>
          </Flex>
        </div>
        {/* inbound change here tag with parent */}
        {custody?.status_info?.description === 'Needs attention' &&
        custody?.type === CoCEventType.AiCallAlert ? (
          <Tag className=' mt-2 mb-2 one-time-status-tag need-attention'>
            Needs attention
          </Tag>
        ) : null}

        {custody?.type === CoCEventType.OneTimeNotification && (
          <OneTimeCOCAlert
            data={custody}
            getFormattedTime={getFormattedTime}
            setShowFull={setShowFull}
            showFull={showFull}
            shipmentId={shipmentId}
            toggleDrawer={toggleDrawer}
            parentRef={parentRef}
            repliesRef={repliesRef}
            fetchSingleShipmentData={fetchSingleShipmentData}
            canViewTranscripts={canViewTranscripts}
            canEditAiCommunications={canEditAiCommunications}
          />
        )}

        {custody?.type === CoCEventType.CustomerNotification && (
          <CustomerNotificationCOCRow
            data={custody}
            parentRef={parentRef}
            repliesRef={repliesRef}
          />
        )}

        {custody?.type !== CoCEventType.CustomerNotification && (
          <CustomShipmentText
            text={String(custody?.comment || '')}
            color='var(--text-light)'
            marginBottom='4px'
          />
        )}

        {custody?.type !== CoCEventType.CustomerNotification && (
          <>
            {custody?.location_is_plain_text ? (
              <div ref={repliesRef} className='reply-comment-message'>
                {custody?.handler === 'AWB' && custody?.location
                  ? `${custody.location} • [AWB]`
                  : (custody?.location ?? '')}
              </div>
            ) : (
              <div
                ref={repliesRef}
                className='reply-comment-message'
                dangerouslySetInnerHTML={{
                  __html:
                    custody?.handler === 'AWB' && custody?.location
                      ? `${custody.location} • [AWB]`
                      : custody?.location,
                }}
              />
            )}
            {/* gxp note  */}
            {custody?.gxp_note && (
              <CustomShipmentText
                htmlString={`"${custody?.gxp_note || ''}"`}
                color='var(--text-light)'
                whiteSpace='pre-line'
                marginTop={'0px'}
                marginBottom={'0px'}
              />
            )}
          </>
        )}
        {canViewTranscripts &&
        custody?.type === CoCEventType.AiCallAlert &&
        custody?.has_transcript ? (
          <CustomButton
            text='View call transcript'
            bgColor='var(--light-gray)'
            marginTop='10px'
            color='black'
            fontSize='12px'
            fontWeight='600'
            borderRadius='28px'
            width='128px'
            boxShadow='none'
            height='28px'
            onClick={() => {
              toggleDrawer?.('open', 'callTranscripts', shipmentId, {
                ...custody,
                transcriptID: custody?.transcript_id,
              });
            }}
          />
        ) : null}
      </div>
    </>
  );
};

export default TimelineItemCT;
